import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSpring, animated } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useCart } from '../../hooks/useCart';

interface ProductCartItemProps {
  id: number;
  setIsDrawerOpen: (state: boolean) => void;
  img: string;
  title: string;
  quantity: number;
  price: number;
}

export const ProductCartItem: React.FC<ProductCartItemProps> = ({
  id,
  setIsDrawerOpen,
  img,
  title,
  quantity,
  price,
}) => {
  const { onDecreaseQuantity, onIncreaseQuantity, isRemoved, onRemoveFromCartDelayed } = useCart({ animationDelay: 300 });

  // Стейт для отслеживания свайпа
  const [isSwiped, setIsSwiped] = useState(false);
  const navigate = useNavigate();
  // Инициализация React Spring
  const [{ x, opacity }, api] = useSpring(() => ({
    x: 0,
    opacity: 1,
  }));

  useEffect(() => {
    if (isRemoved) api.start({ x: -120, opacity: 0, immediate: false });
  }, [isRemoved]);

  const bind = useDrag(
    ({ movement: [mx], down, cancel, event }) => {
      // Останавливаем стандартное поведение и распространение события


      // Ограничиваем движение только влево
      const allowedX = Math.min(mx, 0); // Обрезаем положительные значения mx (движение вправо)

      // Рассчитываем opacity в зависимости от перемещения
      const newOpacity = Math.max(1 - Math.abs(allowedX) / 80, 0); // Прозрачность уменьшается при движении влево
      setIsSwiped(true);
      if (allowedX < -40 && !down) {
        cancel?.(); // Прерываем дальнейшее движение
        api.start({ x: -120, opacity: 0, immediate: false }); // Плавное исчезновение и движение
        onRemoveFromCartDelayed(id);
      } else if (!down) {
        // Возвращаем в исходное положение, если свайп не завершен
        api.start({ x: 0, opacity: 1, immediate: false });
    
      } else {
        api.start({ x: allowedX, opacity: newOpacity, immediate: true });
      }
      if(!down)
        setTimeout(()=> setIsSwiped(false),10)
    },
    {
      axis: 'x', // Движение только по оси X
      filterTaps: false, // Фильтрация тапов отключена
      preventScroll: false,
    }
  );

  
  const handleLinkClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!isSwiped) {
      navigate(`/product/${id}`);
      setIsDrawerOpen(false);
    }
  };

  return (
    <div className="relative">
      <animated.div
        {...bind()} // Подключаем свайп
        style={{
          x, // Применяем анимацию для позиции элемента
          opacity, // Применяем анимацию для прозрачности
        }} // Применяем React Spring для стилей
        className={`w-full touch-pan-y bg-heroGray px-2 xs:px-4 py-3 flex items-center relative mb-2`}
      >
        <button
          onClick={(e) => handleLinkClick(e)} // Блокируем переход, если свайп был выполнен
          className="flex items-center"
        >
          <div
            className="w-[70px] shrink-0 xs:w-[90px] h-[70px] xs:h-[90px] rounded-md mr-2 xs:mr-4 bg-cover"
            style={{ backgroundImage: `url(${img})` }}
          ></div>
          <div className="text-xs">
            <p className="text-white xs:text-base min-w-[60px] max-w-[40px] select-none">{title}</p>
          </div>
        </button>
        <p className="text-buttonPink font-bold text-lg xs:text-[22px]  leading-6 xs:mr-3 select-none ml-auto ">{price}$</p>
        <div className="flex items-center gap-2 select-none ml-auto">
          <button className="text-2xl text-white" onClick={() => !isSwiped && onDecreaseQuantity(id, quantity)}>
            -
          </button>
          <p className="text-white font-bold text-lg xs:text-[22px] leading-6">{quantity}</p>
          <button className="text-2xl text-white" onClick={() => !isSwiped && onIncreaseQuantity(id)}>
            +
          </button>
        </div>
        <button className="group hidden cart-xs:block cart-xs:ml-5" onClick={() => onRemoveFromCartDelayed(id)}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="stroke-current text-gray-400 group-hover:text-buttonPink transition-colors duration-300"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4.375 4.375L15.625 15.625" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4.375 15.625L15.625 4.375" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </animated.div>
      {!isRemoved && (
        <div className="absolute right-2 -z-10 top-1/2 transform -translate-y-1/2">
          <img src="images/redCross.png" alt="Red cross" />
        </div>
      )}
    </div>
  );
};
